// apps/web/src/shared/schemas/step2.ts
import { z } from "zod";

const requiredMsg = (key: string) => ({ message: key });

const digits9 = z
  .string()
  .trim()
  .min(1, requiredMsg("onboarding.validation.vatNumberRequired"))
  .regex(/^\d{9}$/, {
    message: "onboarding.validation.vatNumberInvalidFormat",
  });

const streetNumber = z
  .string()
  .trim()
  .min(1, requiredMsg("onboarding.validation.streetNumberRequired"))
  .regex(/^\d+$/, {
    message: "onboarding.validation.streetNumberInvalidFormat",
  });

export const Step2Schema = z
  .object({
    vatNumber: digits9,

    billingStreet: z
      .string()
      .trim()
      .min(1, requiredMsg("onboarding.validation.streetRequired")),
    billingStreetNumber: streetNumber,
    billingStreetNumberSuffix: z.string().trim().optional().or(z.literal("")),
    billingPostalCode: z
      .string()
      .trim()
      .min(1, requiredMsg("onboarding.validation.postalCodeRequired")),
    billingCity: z
      .string()
      .trim()
      .min(1, requiredMsg("onboarding.validation.cityRequired")),
    billingDistrict: z.string().trim().optional().or(z.literal("")),
    billingCountry: z
      .string()
      .trim()
      .min(1, requiredMsg("onboarding.validation.countryRequired")),

    differentCompanyAddress: z.boolean(),

    companyStreet: z.string().trim().optional().or(z.literal("")),
    companyStreetNumber: z.string().optional().or(z.literal("")),
    companyStreetNumberSuffix: z.string().trim().optional().or(z.literal("")),
    companyPostalCode: z.string().trim().optional().or(z.literal("")),
    companyCity: z.string().trim().optional().or(z.literal("")),
    companyDistrict: z.string().trim().optional().or(z.literal("")),
    companyCountry: z.string().trim().optional().or(z.literal(""))
  })
  .superRefine((data, ctx) => {
    if (!data.differentCompanyAddress) return;

    const require = (value: string | undefined, path: (keyof typeof data)[], message: string) => {
      if (!String(value ?? "").trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path,
          message
        });
      }
    };

    require(data.companyStreet, ["companyStreet"], "onboarding.validation.streetRequired");
    require(data.companyStreetNumber, ["companyStreetNumber"], "onboarding.validation.streetNumberRequired");
    require(data.companyPostalCode, ["companyPostalCode"], "onboarding.validation.postalCodeRequired");
    require(data.companyCity, ["companyCity"], "onboarding.validation.cityRequired");
    require(data.companyCountry, ["companyCountry"], "onboarding.validation.countryRequired");

    const companyStreetNumberValue = String(data.companyStreetNumber ?? "").trim();
    if (companyStreetNumberValue && !/^\d+$/.test(companyStreetNumberValue)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["companyStreetNumber"],
        message: "onboarding.validation.streetNumberInvalidFormat"
      });
    }
  });

export type Step2Input = z.infer<typeof Step2Schema>;
